import React from 'react';
import Icon from '../Icon';
import '../../styles/components/ContactCard.css';

export interface ContactCardMetaRow {
  icon?: string;
  label: string;
  href?: string;
}

export interface ContactCardTag {
  label: string;
  /** Named palette key (see .cc-tag-* in ContactCard.css) or a literal hex. */
  color?: string;
}

export interface ContactCardAction {
  icon: string;
  onClick: () => void;
  title?: string;
  className?: string;
}

export interface ContactCardProps {
  /** Avatar photo URL. Falls back to `initials` on a solid color chip when omitted. */
  avatar?: string;
  initials?: string;
  avatarColor?: string;
  name: string;
  subtitle?: string;
  metaRows?: ContactCardMetaRow[];
  tags?: Array<string | ContactCardTag>;
  actions?: ContactCardAction[];
  customContent?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

/**
 * Avatar/dot + name + contact-metadata card — for cards whose subject is a
 * *person* (or person-shaped record), never a priced or line-itemized thing.
 * Extracted from three near-identical hand-rolled cards (drivers, guests,
 * team members) that each carried the same shape with different CSS.
 */
const ContactCard: React.FC<ContactCardProps> = ({
  avatar,
  initials,
  avatarColor,
  name,
  subtitle,
  metaRows,
  tags,
  actions,
  customContent,
  onClick,
  className = '',
}) => {
  const tagContent = (tag: string | ContactCardTag) =>
    typeof tag === 'string' ? { label: tag, color: undefined } : tag;

  return (
    <div className={`cc-card ${className}`} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
      {avatar ? (
        <img className="cc-avatar" src={avatar} alt="" />
      ) : (
        <div className="cc-avatar cc-avatar-initials" style={avatarColor ? { background: avatarColor } : undefined}>
          {initials ?? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="cc-body">
        <p className="cc-name">{name}</p>
        {subtitle && <p className="cc-subtitle">{subtitle}</p>}
        {metaRows && metaRows.length > 0 && (
          <div className="cc-meta">
            {metaRows.map((row, i) => (
              <div key={i} className="cc-meta-row">
                {row.icon && <Icon name={row.icon} size={13} />}
                {row.href ? (
                  <a href={row.href} className="cc-meta-link" onClick={e => e.stopPropagation()}>{row.label}</a>
                ) : (
                  <span>{row.label}</span>
                )}
              </div>
            ))}
          </div>
        )}
        {tags && tags.length > 0 && (
          <div className="cc-tags">
            {tags.map((tag, i) => {
              const { label, color } = tagContent(tag);
              const isHex = color?.startsWith('#');
              return (
                <span
                  key={i}
                  className={`cc-tag${!isHex ? ` cc-tag-${color ?? 'neutral'}` : ''}`}
                  style={isHex ? { background: color, color: '#fff' } : undefined}
                >
                  {label}
                </span>
              );
            })}
          </div>
        )}
        {customContent}
      </div>
      {actions && actions.length > 0 && (
        <div className="cc-actions" onClick={e => e.stopPropagation()}>
          {actions.map((action, i) => (
            <button key={i} type="button" className={`cc-action-btn ${action.className ?? ''}`} title={action.title} onClick={action.onClick}>
              <Icon name={action.icon} size={13} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactCard;
